import 'Components/vm-link-overlay/vm-link-overlay';
import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { pluralize } from 'Utils/text';
import placeholderSrc from 'Static/gfx/related/video.svg';
import Styles from './vm-video-card-2025-12-1-long.scss';

/**
 * Long ("media") video-card layout — the web-component port of the
 * `templates/video/v2025_12_1/video_item_media.html` Django partial.
 *
 * Renders the `.video-item` markup: a left-hand 300px thumbnail (with optional
 * duration overlay or placeholder) beside an info column holding the author
 * heading, the title, and a "Featuring" contributor line.
 *
 * The component owns the visual layout, link wrapping, and the
 * placeholder/duration affordances; the host supplies the content via
 * attributes, with named slots to override the author and contributor markup.
 *
 * The whole card is clickable: a `vm-link-overlay` stretches a transparent
 * primary link over the entire `.video-item`, while the existing thumbnail,
 * title, publisher, and contributor anchors stay independently clickable
 * (elevated above the overlay). This adds no visual change — the thumbnail and
 * title keep their own anchors so their styling/hover behaviour is unchanged.
 *
 * @attr href           - Link destination for the thumbnail and title.
 * @attr heading        - Visible title text (rendered as h2).
 * @attr thumbnail-src  - Thumbnail image URL. If empty, a placeholder is shown.
 * @attr duration-label   - Pre-formatted duration (e.g. "12:34") shown over the
 *                           thumbnail. Component does not format; caller decides.
 * @attr publisher        - JSON `{name, href?}` object for the author/channel
 *                           heading. Rendered as plain text or a link when `href`
 *                           is present.
 * @attr contributors     - JSON array of `{name, href?}` items rendered after a
 *                           leading "Featuring" label, comma-separated, each linked
 *                           when `href` is present.
 * @attr views-count      - Raw view count. Shown (comma-grouped) in the stats row
 *                           with an eye icon. Hidden when 0.
 * @attr created-at       - ISO-8601 publish timestamp. Shown as an absolute date
 *                           (e.g. "January 5, 2026") with a calendar icon.
 * @attr thumbs-up-count  - Raw thumbs-up count. Shown (comma-grouped) in the stats
 *                           row with a thumbs-up icon. Hidden when 0.
 * @attr target           - Anchor `target` for the thumbnail and title links.
 * @slot publisher    - Override for the default author rendering.
 * @slot contributors - Override for the default "Featuring" contributors rendering.
 */
@customElement('vm-video-card-2025-12-1-long')
export class VmVideoCard extends LitElement {
    static override styles = Styles;

    @property({ type: String }) href = '';
    @property({ type: String }) heading = '';
    @property({ type: String, attribute: 'thumbnail-src' }) thumbnailSrc = '';
    @property({ type: String, attribute: 'duration-label' }) durationLabel = '';
    @property({ type: Object }) publisher: { name: string; href?: string } | null = null;
    @property({ type: Array }) contributors: Array<{ name: string; href?: string }> = [];
    @property({ type: Number, attribute: 'views-count' }) viewsCount = 0;
    @property({ type: String, attribute: 'created-at' }) createdAt = '';
    @property({ type: Number, attribute: 'thumbs-up-count' }) thumbsUpCount = 0;
    @property({ type: String }) target: HTMLAnchorElement['target'] = '';

    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-video-card');
        this.dataset.testid = 'vm-video-card';
    }

    private _renderDefaultPublisher() {
        if (!this.publisher?.name) return html`Video`;
        const { name, href } = this.publisher;
        return href ? html`<a href=${href} title=${name}>${name}</a>` : html`${name}`;
    }

    private _renderDefaultContributors() {
        if (!this.contributors.length) return nothing;
        // Matches the template's "Featuring Name1, Name2" byline. Each contributor
        // is a link when an `href` is supplied, otherwise plain text.
        return html`
            Featuring
            ${this.contributors.map((contributor, i) => {
                const separator = i < this.contributors.length - 1 ? ', ' : '';
                return contributor.href
                    ? html`<a href=${contributor.href}>${contributor.name}</a>${separator}`
                    : html`<span class="contributor-text">${contributor.name}</span>${separator}`;
            })}
        `;
    }

    private _formatDate(iso: string) {
        const date = new Date(iso);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    private _renderMeta() {
        // Mirrors `content_views_long.html`: an inline list of views / publish
        // date / thumbs-up, the date floated right via `pull-right`. Each stat is
        // paired with its Font Awesome icon (the document-level FontAwesome face
        // is reachable from the shadow DOM). Empty stats are dropped so no lone
        // icon is shown.
        const hasViews = this.viewsCount > 0;
        const hasThumbs = this.thumbsUpCount > 0;
        const date = this.createdAt ? this._formatDate(this.createdAt) : '';
        if (!hasViews && !hasThumbs && !date) return nothing;
        return html`
            <div class="video-item__meta mt-2">
                <ul class="list-inline d-inline text-muted">
                    ${hasViews
                        ? html`<li class="list-inline-item mr-2 video-item__number-of-views">
                              <span class="fa fa-eye" aria-hidden="true"></span>
                              ${this.viewsCount.toLocaleString('en-US')} view${pluralize(this.viewsCount)}
                          </li>`
                        : nothing}
                    ${date
                        ? html`<li class="list-inline-item mr-2 pull-right video-item__created-date">
                              <span class="fa fa-calendar" aria-hidden="true"></span> ${date}
                          </li>`
                        : nothing}
                    ${hasThumbs
                        ? html`<li class="list-inline-item mr-2 video-item__number-of-likes">
                              ${this.thumbsUpCount.toLocaleString('en-US')}
                              <span class="fa fa-thumbs-o-up thumbup-count-icon" aria-hidden="true"></span>
                          </li>`
                        : nothing}
                </ul>
            </div>
        `;
    }

    private _renderThumbnail() {
        if (this.thumbnailSrc) {
            return html`
                <img src=${this.thumbnailSrc} alt="" class="video-item__img" />
                ${this.durationLabel
                    ? html`<span class="overlay video-duration">${this.durationLabel}</span>`
                    : nothing}
            `;
        }
        return html`
            <img src=${placeholderSrc} alt="" class="video-item__img video-item__img-placeholder" />
            <span class="overlay video-duration info-overlay"><small>VIDEO</small></span>
        `;
    }

    override render() {
        return html`
            <vm-link-overlay>
                <div class="video-item">
                    <div class="video-item__img-container video-item__img-container-center">
                        <a href=${this.href} target=${this.target || nothing} aria-label=${this.heading}>
                            ${this._renderThumbnail()}
                        </a>
                    </div>
                    <div class="video-item__info">
                        <h3 class="video-item__author">
                            <slot name="publisher">${this._renderDefaultPublisher()}</slot>
                        </h3>
                        <h2 class="video-item__title">
                            <a href=${this.href} target=${this.target || nothing}>${this.heading}</a>
                        </h2>
                        <div class="video-item__desc">
                            <slot name="contributors">${this._renderDefaultContributors()}</slot>
                        </div>
                        ${this._renderMeta()}
                    </div>
                </div>
                <a
                    slot="link-overlay"
                    href=${this.href}
                    target=${this.target || nothing}
                    class="video-item__link"
                    aria-label=${this.heading}
                ></a>
            </vm-link-overlay>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-card-2025-12-1-long': VmVideoCard;
    }
}
